import React, { useEffect, useRef, useState } from 'react';
import {
  useBackgroundStore,
  useActiveBackground,
  computeRenderLayers,
  layerBgValue,
} from './BackgroundProvider';

export default function BackgroundStage({ className, maxOffset = 20 }) {
  const { state, resolveSrc } = useBackgroundStore();
  const { bg } = useActiveBackground();
  const rootRef = useRef(null);
  const mouse = useRef({ tx: 0, ty: 0, x: 0, y: 0 });
  const rafRef = useRef(0);
  const [prefersReduced, setPrefersReduced] = useState(false);
  // Fade each background in rather than letting it pop into place. The store
  // resolves no background at all until the org settings load, so without this
  // the correct image lands as a hard cut a moment after the page appears.
  const [visible, setVisible] = useState(false);
  const bgId = bg?.id || null;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const upd = () => setPrefersReduced(mq.matches);
    upd();
    mq.addEventListener('change', upd);
    return () => mq.removeEventListener('change', upd);
  }, []);

  const parallaxOn =
    state.parallaxEnabled && !(state.respectReducedMotion && prefersReduced);
  const intensity = (bg?.settings?.parallax ?? 100) / 100;
  // Static and video backgrounds disable the mouse-follow parallax entirely.
  const bgParallaxOn =
    parallaxOn && bg?.kind !== 'static' && bg?.kind !== 'video';

  useEffect(() => {
    if (bg) document.body.classList.add('emhub-bg-active');
    else document.body.classList.remove('emhub-bg-active');
    return () => document.body.classList.remove('emhub-bg-active');
  }, [bg]);

  useEffect(() => {
    if (!bg) {
      setVisible(false);
      return undefined;
    }
    // One frame at opacity 0 so the transition has something to animate from.
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgId]);

  useEffect(() => {
    if (!bgParallaxOn) return;
    const onMove = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      mouse.current.tx = (e.clientX / w - 0.5) * 2;
      mouse.current.ty = (e.clientY / h - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const loop = () => {
      const m = mouse.current;
      m.x += (m.tx - m.x) * 0.08;
      m.y += (m.ty - m.y) * 0.08;
      const el = rootRef.current;
      if (el) {
        el.style.setProperty('--mx', m.x.toFixed(4));
        el.style.setProperty('--my', m.y.toFixed(4));
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('mousemove', onMove);
    };
  }, [bgParallaxOn]);

  if (!bg) return null;
  const s = bg.settings || {};
  const layers = computeRenderLayers(bg);
  const isVideoBg = bg.kind === 'video' && bg.mediaType === 'video';

  return (
    <div
      ref={rootRef}
      className={className}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        overflow: 'hidden',
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: 'opacity 500ms ease',
        '--mx': '0',
        '--my': '0',
      }}
    >
      {layers.map((L) => {
        const mult = (L.depth / 100) * intensity * maxOffset;
        const resolved = resolveSrc(L.src);
        const transform = `translate3d(calc(var(--mx) * -${mult}px + ${L.offsetX || 0}px), calc(var(--my) * -${mult}px + ${L.offsetY || 0}px), 0) scale(${L.scale / 100}) rotate(${L.rotate || 0}deg)`;
        const commonStyle = {
          position: 'absolute',
          inset: `-${Math.max(mult * 2, 40)}px`,
          transform,
          transformOrigin: 'center',
          filter: L.blur ? `blur(${L.blur}px)` : 'none',
          opacity: (L.opacity ?? 100) / 100,
          mixBlendMode: L.blend && L.blend !== 'normal' ? L.blend : undefined,
          willChange: 'transform',
          transition: bgParallaxOn ? 'none' : 'transform 600ms ease-out',
        };
        if (isVideoBg) {
          return (
            <video
              key={L.id}
              src={resolved || ''}
              autoPlay
              loop
              muted
              playsInline
              style={{
                ...commonStyle,
                objectFit: 'cover',
                width: 'auto',
                height: 'auto',
                minWidth: '100%',
                minHeight: '100%',
              }}
            />
          );
        }
        return (
          <div
            key={L.id}
            style={{
              ...commonStyle,
              background: layerBgValue(resolved),
            }}
          />
        );
      })}
      {s.dim > 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `rgba(0,0,0,${s.dim / 100})`,
          }}
        />
      )}
      {s.overlayAlpha > 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: s.overlayColor || '#ffffff',
            opacity: s.overlayAlpha / 100,
          }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at center, transparent 55%, rgba(10,10,10,0.15) 100%)',
        }}
      />
    </div>
  );
}
